/**
 * @lodex-pro/web-portal/hooks/useRequestorLinks — operator portal rework PR 10: requestor →
 * pumper matches.
 *
 * Haulers record who requests loads (their requestors, or the requester fields typed on a
 * job). On a pumper's or foreman's first sign-in those records are matched to them by email
 * or phone. The admin reviews the matches (and settles conflicts); a pumper or foreman is
 * OFFERED the tanks at the wells they request loads for. An offer is never selected for them:
 * the pumper's own choice sets responsibility.
 *
 * Reads go through `useApiQuery`; writes use `useMutation` over `authedFetch` and throw
 * `ApiError` (status + catalog code), like useTankSelections.
 *
 * Types only from @lodex-pro/shared (DEC-149 §9).
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type {
  DecideRequestorLinkRequest,
  RequestorLinkView,
  RequestorWellOfferView,
} from '@lodex-pro/shared/validation/operator-portal-routes';

const BASE = '/v1/operator-portal';

// ── Query keys ───────────────────────────────────────────────────────────────

export const REQUESTOR_LINKS_KEY: readonly string[] = ['operator-portal', 'requestor-links'];
export const REQUESTOR_OFFERS_KEY: readonly string[] = ['operator-portal', 'my-tanks-offers'];

// ── Reads ────────────────────────────────────────────────────────────────────

/** Every match in the company, conflicts first (admin only). */
export function useRequestorLinks(
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ links: RequestorLinkView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ links: RequestorLinkView[] }>(opts, `${BASE}/requestor-links`, {
    queryKey: REQUESTOR_LINKS_KEY,
    staleTime: 30_000,
    enabled,
  });
}

/** Tanks at the wells the caller requests loads for, not yet selected (pumper/foreman). */
export function useRequestorOffers(
  enabled: boolean,
): ReturnType<typeof useApiQuery<{ offers: RequestorWellOfferView[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ offers: RequestorWellOfferView[] }>(opts, `${BASE}/my-tanks/offers`, {
    queryKey: REQUESTOR_OFFERS_KEY,
    staleTime: 30_000,
    enabled,
  });
}

// ── Writes ───────────────────────────────────────────────────────────────────

async function postJson<T>(
  opts: ReturnType<typeof useApiOpts>,
  path: string,
  body: unknown,
): Promise<T> {
  const res = await authedFetch(opts, `${BASE}${path}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw await apiErrorFromResponse(res, path);
  }
  return (await res.json()) as T;
}

export interface DecideRequestorLinkVars {
  readonly id: string;
  readonly decision: DecideRequestorLinkRequest['decision'];
}

/** LINK = "this pairing is right" (dismisses the competing rows); DISMISS = "not them". */
export function useDecideRequestorLink(): UseMutationResult<
  { ok: true },
  Error,
  DecideRequestorLinkVars
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, DecideRequestorLinkVars>({
    mutationFn: async (vars: DecideRequestorLinkVars): Promise<{ ok: true }> => {
      const body: DecideRequestorLinkRequest = { decision: vars.decision };
      return postJson<{ ok: true }>(
        opts,
        `/requestor-links/${encodeURIComponent(vars.id)}/decide`,
        body,
      );
    },
    onSettled: (): void => {
      void qc.invalidateQueries({ queryKey: REQUESTOR_LINKS_KEY });
    },
  });
}

/** Re-run the match for the whole company. Admin decisions survive a re-run. */
export function useRerunRequestorMatch(): UseMutationResult<{ links: number }, Error, void> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<{ links: number }, Error, void>({
    mutationFn: async (): Promise<{ links: number }> =>
      // `{}`, not no body: authedFetch sends Content-Type: application/json, and Fastify
      // refuses an empty JSON body.
      postJson<{ links: number }>(opts, '/requestor-links/rerun', {}),
    onSettled: (): void => {
      void qc.invalidateQueries({ queryKey: REQUESTOR_LINKS_KEY });
    },
  });
}
